import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Icon add-to-cart for the MOVA collection section.
 * Dispatches CartLinesUpdateEvent so the cart drawer refreshes / auto-opens.
 */
class MovaCollectionAtc extends HTMLElement {
  connectedCallback() {
    this.#button = this.querySelector('[data-mova-collection-atc]');
    if (!this.#button) return;
    this.#button.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
  }

  /** @type {HTMLButtonElement | null} */
  #button = null;

  #onClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const button = this.#button;
    if (!button || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const quantity = Number(button.dataset.quantity) || 1;
    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(quantity));
    formData.append('sections_url', window.location.pathname);

    const cartItems = document.querySelectorAll('cart-items-component[data-section-id]');
    const sectionIds = [];
    cartItems.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        deferred.resolve({
          cart: null,
          detail: { didError: true, source: 'mova-collection-atc' },
        });
        return;
      }

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'mova-collection-atc',
          itemCount: quantity,
        },
      });
    } catch (error) {
      deferred.reject(error);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('mova-collection-atc')) {
  customElements.define('mova-collection-atc', MovaCollectionAtc);
}
